import React, { FC } from 'react';
import 'chart.js/auto';
import { Bar, Line, Pie, Doughnut } from 'react-chartjs-2';

/**
 * Renders a chart that the agent describes as a JSON spec inside a fenced
 * ```chart block. See docs/agent/system-prompt.md (PART 3) for the contract.
 */

export type ChartType = 'bar' | 'line' | 'pie' | 'doughnut';

export interface ChartSpec {
  type: ChartType;
  title: string;
  labels: string[];
  series: { name: string; data: number[] }[];
  stacked: boolean;
}

const MAX_LABELS = 12;
const MAX_SERIES = 5;
const CHART_TYPES: ChartType[] = ['bar', 'line', 'pie', 'doughnut'];
const PALETTE = [
  '#E60000',
  '#414141',
  '#841810',
  '#999999',
  '#FF8A8A',
  '#B0B0B0',
  '#5C0A05',
  '#D9D9D9',
  '#FFB3B3',
  '#666666',
  '#A80000',
  '#F5C6C6',
];

const COMPLETE_BLOCK = /```chart\s*([\s\S]*?)```/;
const UNFINISHED_BLOCK = /```chart[\s\S]*$/;

const isFiniteNumber = (v: unknown): v is number =>
  typeof v === 'number' && Number.isFinite(v);

const validateChart = (raw: any): ChartSpec | null => {
  if (!raw || typeof raw !== 'object') return null;
  if (!CHART_TYPES.includes(raw.type)) return null;
  if (
    !Array.isArray(raw.labels) ||
    raw.labels.length < 1 ||
    raw.labels.length > MAX_LABELS
  )
    return null;
  if (
    !Array.isArray(raw.series) ||
    raw.series.length < 1 ||
    raw.series.length > MAX_SERIES
  )
    return null;

  const labels: string[] = raw.labels.map((l: unknown) => String(l));
  const series: ChartSpec['series'] = [];
  for (let i = 0; i < raw.series.length; i += 1) {
    const s = raw.series[i];
    if (!s || !Array.isArray(s.data) || s.data.length !== labels.length)
      return null;
    if (!s.data.every(isFiniteNumber)) return null;
    series.push({ name: String(s.name ?? `Series ${i + 1}`), data: s.data });
  }

  return {
    type: raw.type,
    title: typeof raw.title === 'string' ? raw.title.slice(0, 80) : '',
    labels,
    series,
    stacked: raw.stacked === true,
  };
};

/**
 * Splits an agent reply into the text to show and the chart to draw.
 * The raw JSON block is always removed from the text, including a block that is
 * still being streamed and has no closing fence yet.
 */
export const splitChart = (
  message: string,
): { text: string; chart: ChartSpec | null } => {
  const match = COMPLETE_BLOCK.exec(message);
  if (!match) {
    return { text: message.replace(UNFINISHED_BLOCK, '').trim(), chart: null };
  }
  let chart: ChartSpec | null = null;
  try {
    chart = validateChart(JSON.parse(match[1]));
  } catch {
    chart = null; // invalid JSON: show the text without a chart
  }
  return { text: message.replace(COMPLETE_BLOCK, '').trim(), chart };
};

const containerStyle: React.CSSProperties = {
  width: 260,
  height: 220,
  margin: '8px 0 2px',
};

export const ChatChart: FC<{ spec: ChartSpec }> = ({ spec }) => {
  const { type, title, labels, series, stacked } = spec;
  const isRound = type === 'pie' || type === 'doughnut';

  const baseOptions: any = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      title: { display: !!title, text: title },
      legend: {
        display: isRound || series.length > 1,
        position: 'bottom' as const,
        labels: { boxWidth: 12 },
      },
    },
  };

  if (isRound) {
    const data = {
      labels,
      datasets: [
        {
          label: series[0].name,
          data: series[0].data,
          backgroundColor: labels.map((_, i) => PALETTE[i % PALETTE.length]),
          borderColor: '#ffffff',
        },
      ],
    };
    return (
      <div style={containerStyle}>
        {type === 'pie' ? (
          <Pie data={data} options={baseOptions} />
        ) : (
          <Doughnut data={data} options={baseOptions} />
        )}
      </div>
    );
  }

  const data = {
    labels,
    datasets: series.map((s, i) => ({
      label: s.name,
      data: s.data,
      backgroundColor: PALETTE[i % PALETTE.length],
      borderColor: PALETTE[i % PALETTE.length],
      ...(type === 'line' ? { fill: false, tension: 0.3 } : {}),
    })),
  };
  const options = {
    ...baseOptions,
    scales: {
      x: { stacked },
      y: { stacked, beginAtZero: true },
    },
  };

  return (
    <div style={containerStyle}>
      {type === 'line' ? (
        <Line data={data} options={options} />
      ) : (
        <Bar data={data} options={options} />
      )}
    </div>
  );
};
